<template>
  <v-app>
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      @navigation="handleNavigation"
    />
    
    <v-main>
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold primary--text">Payouts</h1>
            <p class="text-subtitle-1 text-grey-600 mt-2">Track your commission payments and earnings</p>
          </div>
          <div class="d-flex align-center">
            <v-btn color="green" dark class="mr-3" @click="showGenerateInvoiceDialog = true">
              <span class="material-icons-round mr-2">receipt</span>
              Generate Invoice
            </v-btn>
            <v-btn color="primary" outlined class="mr-3" @click="exportData">
              <span class="material-icons-round mr-2">file_download</span>
              Export
            </v-btn>
            <v-btn color="blue" dark @click="downloadReport">
              <span class="material-icons-round mr-2">description</span>
              Download Report
            </v-btn>
          </div>
        </div>

        <!-- Statistics Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Pending Commissions</div>
              <div class="text-h4 font-weight-bold primary--text">${{ formatCurrency(stats.pendingCommissions) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Paid This Month</div>
              <div class="text-h4 font-weight-bold success--text">${{ formatCurrency(stats.paidThisMonth) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Avg Processing Time</div>
              <div class="text-h4 font-weight-bold info--text">{{ stats.avgProcessingTime }} days</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card elevation="3" class="pa-4 text-center">
              <div class="text-h6 font-weight-bold text-grey-700 mb-2">Disputes</div>
              <div class="text-h4 font-weight-bold error--text">{{ stats.disputes }} open</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Payouts Table -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold">
            <span class="material-icons-round mr-2">payment</span>
            Commission History
          </v-card-title>
          <v-card-text class="pa-0">
            <div v-if="loading" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <div class="mt-4 text-h6">Loading payouts...</div>
            </div>

            <div v-else-if="payouts.length === 0" class="text-center pa-8">
              <span class="material-icons-round" style="font-size: 64px; color: #ccc;">payment</span>
              <div class="mt-4 text-h6 text-grey-600">No payouts found</div>
              <div class="text-body-2 text-grey-500">Commission payments will appear here once processed</div>
            </div>

            <div v-else>
              <v-data-table
                :headers="headers"
                :items="payouts"
                :items-per-page="10"
                class="elevation-0"
                :loading="loading"
              >
                <template v-slot:item.date="{ item }">
                  <span class="font-weight-medium">{{ formatDate(item.date) }}</span>
                </template>

                <template v-slot:item.referring_agent="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="mr-3">
                      <span class="white--text text-caption">{{ getInitials(item.referring_agent) }}</span>
                    </v-avatar>
                    <span class="font-weight-medium">{{ item.referring_agent }}</span>
                  </div>
                </template>

                <template v-slot:item.vehicle="{ item }">
                  <div>
                    <div class="font-weight-medium">{{ item.vehicle }}</div>
                    <div class="text-caption text-grey-600">{{ item.vehicle_details || '' }}</div>
                  </div>
                </template>

                <template v-slot:item.commission="{ item }">
                  <span class="font-weight-bold text-h6">${{ formatCurrency(item.commission) }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    dark
                    small
                  >
                    <span class="material-icons-round mr-1" style="font-size: 14px;">
                      {{ getStatusIcon(item.status) }}
                    </span>
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Generate Invoice Dialog -->
    <v-dialog v-model="showGenerateInvoiceDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 success--text">
          <span class="material-icons-round mr-2">receipt</span>
          Generate Payout Invoice
        </v-card-title>
        <v-card-text>
          <v-form ref="invoiceForm" v-model="invoiceFormValid">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="invoiceForm.commissionIds"
                  :items="pendingCommissions"
                  item-text="display_text"
                  item-value="id"
                  label="Select Pending Commissions"
                  multiple
                  chips
                  deletable-chips
                  :rules="[v => (v && v.length > 0) || 'Please select at least one commission']"
                  outlined
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 2" small>
                      ${{ formatCurrency(item.amount) }} - {{ item.agent_name }}
                    </v-chip>
                    <span v-if="index === 2" class="text-caption grey--text">
                      (+{{ invoiceForm.commissionIds.length - 2 }} others)
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="invoiceForm.paymentMethod"
                  :items="paymentMethods"
                  label="Payment Method"
                  :rules="[v => !!v || 'Payment method is required']"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="invoiceForm.totalAmount"
                  label="Total Amount"
                  prefix="$"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="invoiceForm.notes"
                  label="Notes (Optional)"
                  placeholder="Add any additional notes for this payout request..."
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeInvoiceDialog">Cancel</v-btn>
          <v-btn
            color="green"
            dark
            @click="generateInvoice"
            :loading="generateInvoiceLoading"
            :disabled="!invoiceFormValid"
          >
            <span class="material-icons-round mr-2">receipt</span>
            Generate Invoice
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '../Layout/HeaderDealerDashboard.vue'
import FooterDealer from '../Layout/FooterDealer.vue'

export default {
  name: 'DealerPayouts',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      loading: false,
      userName: '',
      businessName: '',
      stats: {
        pendingCommissions: 12450,
        paidThisMonth: 34800,
        avgProcessingTime: 2.8,
        disputes: 2
      },
      payouts: [],
      headers: [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Referring Agent', value: 'referring_agent', sortable: true },
        { text: 'Vehicle', value: 'vehicle', sortable: true },
        { text: 'Commission', value: 'commission', sortable: true, align: 'end' },
        { text: 'Status', value: 'status', sortable: true, align: 'center' }
      ],
      showGenerateInvoiceDialog: false,
      generateInvoiceLoading: false,
      invoiceFormValid: false,
      invoiceForm: {
        commissionIds: [],
        paymentMethod: 'bank_transfer',
        totalAmount: '0.00',
        notes: ''
      },
      pendingCommissions: [],
      paymentMethods: [
        { text: 'Bank Transfer', value: 'bank_transfer' },
        { text: 'PayPal', value: 'paypal' },
        { text: 'Check', value: 'check' },
        { text: 'Other', value: 'other' }
      ]
    }
  },
  beforeCreate() {
    const token = localStorage.getItem('token')
    if (!token) {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadUserData()
    await this.loadPayouts()
  },
  computed: {
    selectedCommissionsTotal() {
      if (!this.invoiceForm.commissionIds || this.invoiceForm.commissionIds.length === 0) {
        return 0
      }
      return this.pendingCommissions
        .filter(commission => this.invoiceForm.commissionIds.includes(commission.id))
        .reduce((total, commission) => total + parseFloat(commission.amount), 0)
    }
  },
  watch: {
    selectedCommissionsTotal(newTotal) {
      this.invoiceForm.totalAmount = this.formatCurrency(newTotal)
    }
  },
  methods: {
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
    },
    async loadPayouts() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')

        const response = await axios.get('/api/dealer/payouts', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          const apiPayouts = response.data.data.payouts || []
          const apiStats = response.data.data.stats || {}

          // Use API data if available, otherwise use mock data for demonstration
          if (apiPayouts.length > 0) {
            this.payouts = apiPayouts
            this.stats = {
              pendingCommissions: apiStats.pending_commissions || 0,
              paidThisMonth: apiStats.paid_this_month || 0,
              avgProcessingTime: apiStats.avg_processing_time || 0,
              disputes: apiStats.disputes || 0
            }
          } else {
            // Show mock data for demonstration when no real data exists
            this.payouts = [
              {
                id: 1,
                date: '2025-09-10',
                referring_agent: 'David Jackson',
                vehicle: '2022 Honda Accord',
                vehicle_details: 'EX-L Sedan',
                commission: 500,
                status: 'Paid'
              },
              {
                id: 2,
                date: '2025-09-12',
                referring_agent: 'Malaysia Hannon',
                vehicle: '2019 Dodge Charger',
                vehicle_details: 'SXT AWD',
                commission: 750,
                status: 'Pending'
              },
              {
                id: 3,
                date: '2025-09-14',
                referring_agent: 'John Smith',
                vehicle: '2021 BMW 530i',
                vehicle_details: 'xDrive Sedan',
                commission: 1250,
                status: 'Paid'
              }
            ]
            this.stats = {
              pendingCommissions: 12450,
              paidThisMonth: 34800,
              avgProcessingTime: 2.8,
              disputes: 2
            }
          }

          // Load pending commissions for invoice generation
          await this.loadPendingCommissions()
        } else {
          // Fallback to mock data if API fails
          this.payouts = [
            {
              id: 1,
              date: '2025-09-10',
              referring_agent: 'David Jackson',
              vehicle: '2022 Honda Accord',
              vehicle_details: 'EX-L Sedan',
              commission: 500,
              status: 'Paid'
            },
            {
              id: 2,
              date: '2025-09-12',
              referring_agent: 'Malaysia Hannon',
              vehicle: '2019 Dodge Charger',
              vehicle_details: 'SXT AWD',
              commission: 750,
              status: 'Pending'
            },
            {
              id: 3,
              date: '2025-09-14',
              referring_agent: 'John Smith',
              vehicle: '2021 BMW 530i',
              vehicle_details: 'xDrive Sedan',
              commission: 1250,
              status: 'Paid'
            }
          ]
        }
      } catch (error) {
        console.error('Error loading payouts:', error)
        this.$toast.error('Error loading payout data')

        // Fallback to mock data on error
        this.payouts = [
          {
            id: 1,
            date: '2025-09-10',
            referring_agent: 'David Jackson',
            vehicle: '2022 Honda Accord',
            vehicle_details: 'EX-L Sedan',
            commission: 500,
            status: 'Paid'
          },
          {
            id: 2,
            date: '2025-09-12',
            referring_agent: 'Malaysia Hannon',
            vehicle: '2019 Dodge Charger',
            vehicle_details: 'SXT AWD',
            commission: 750,
            status: 'Pending'
          },
          {
            id: 3,
            date: '2025-09-14',
            referring_agent: 'John Smith',
            vehicle: '2021 BMW 530i',
            vehicle_details: 'xDrive Sedan',
            commission: 1250,
            status: 'Paid'
          }
        ]
      } finally {
        this.loading = false
      }
    },
    formatCurrency(amount) {
      const numAmount = Number(amount) || 0
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numAmount)
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
      })
    },
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'paid': return 'success'
        case 'pending': return 'warning'
        case 'disputed': return 'error'
        case 'processing': return 'info'
        default: return 'grey'
      }
    },
    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case 'paid': return 'check_circle'
        case 'pending': return 'schedule'
        case 'disputed': return 'error'
        case 'processing': return 'sync'
        default: return 'help'
      }
    },
    async exportData() {
      try {
        this.$toast.info('Exporting payout data...')
        const token = localStorage.getItem('token')

        const response = await axios.post('/api/dealer/export-payouts', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Export completed successfully!')
          // In a real implementation, this would trigger a file download
        } else {
          this.$toast.error('Export failed')
        }
      } catch (error) {
        console.error('Error exporting data:', error)
        this.$toast.error('Error exporting payout data')
      }
    },
    downloadReport() {
      // Generate a simple CSV download for now
      const csvContent = this.generateCSV()
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `payouts_report_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$toast.success('Report downloaded successfully!')
    },
    generateCSV() {
      const headers = ['Date', 'Referring Agent', 'Vehicle', 'Commission', 'Status']
      const csvRows = [headers.join(',')]

      this.payouts.forEach(payout => {
        const row = [
          payout.date,
          `"${payout.referring_agent}"`,
          `"${payout.vehicle}"`,
          payout.commission,
          payout.status
        ]
        csvRows.push(row.join(','))
      })

      return csvRows.join('\n')
    },
    async loadPendingCommissions() {
      try {
        const token = localStorage.getItem('token')

        const response = await axios.get('/api/dealer/pending-commissions', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.pendingCommissions = response.data.data.commissions || []
        } else {
          // Mock pending commissions for demonstration
          this.pendingCommissions = [
            {
              id: 1,
              amount: 500,
              agent_name: 'David Jackson',
              description: '2022 Honda Accord Sale',
              display_text: '$500 - David Jackson (2022 Honda Accord)'
            },
            {
              id: 2,
              amount: 750,
              agent_name: 'Malaysia Hannon',
              description: '2019 Dodge Charger Sale',
              display_text: '$750 - Malaysia Hannon (2019 Dodge Charger)'
            }
          ]
        }
      } catch (error) {
        console.error('Error loading pending commissions:', error)
        // Fallback to mock data
        this.pendingCommissions = [
          {
            id: 1,
            amount: 500,
            agent_name: 'David Jackson',
            description: '2022 Honda Accord Sale',
            display_text: '$500 - David Jackson (2022 Honda Accord)'
          },
          {
            id: 2,
            amount: 750,
            agent_name: 'Malaysia Hannon',
            description: '2019 Dodge Charger Sale',
            display_text: '$750 - Malaysia Hannon (2019 Dodge Charger)'
          }
        ]
      }
    },
    closeInvoiceDialog() {
      this.showGenerateInvoiceDialog = false
      this.invoiceForm = {
        commissionIds: [],
        paymentMethod: 'bank_transfer',
        totalAmount: '0.00',
        notes: ''
      }
      this.$refs.invoiceForm?.resetValidation()
    },
    async generateInvoice() {
      try {
        if (!this.$refs.invoiceForm.validate()) {
          return
        }

        this.generateInvoiceLoading = true
        const token = localStorage.getItem('token')

        const response = await axios.post('/api/dealer/generate-payout-invoice', {
          commission_ids: this.invoiceForm.commissionIds,
          payment_method: this.invoiceForm.paymentMethod,
          total_amount: this.selectedCommissionsTotal,
          notes: this.invoiceForm.notes
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Payout invoice generated successfully!')
          this.closeInvoiceDialog()

          // Reload payouts to show the new invoice
          await this.loadPayouts()
        } else {
          this.$toast.error(response.data.message || 'Error generating invoice')
        }
      } catch (error) {
        console.error('Error generating invoice:', error)
        this.$toast.error('Error generating payout invoice')
      } finally {
        this.generateInvoiceLoading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0 0 12px 12px;
}

.agent-item {
  transition: background-color 0.2s ease;
}

.agent-item:hover {
  background-color: #f5f5f5;
}
</style>
